import { addBreadcrumb, captureException, captureMessage, configureScope, init, withScope, reactRouterV5Instrumentation } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import Client from 'components/Client'
import { obscureEmail } from 'lib/utils/string'
import { cloneDeep, get } from 'lodash'
import { sentryFingerprintRules } from './sentryFingerprintRules'
import { isDev, isProductionHost, isStagingHost } from 'lib/env'
import { history } from 'lib/utils/store/navHistory'

import getCurrentUserForSentry from 'graphql/user/getCurrentUserForSentry.gql'

let sentryEnabled = false

export const Severity = Object.freeze({
    Info: 'info',
})

/**
 * Function to check if console logging is enabled based on environment variable
 */
export function consoleLoggingEnabled () {
    return process.env.SENTRY_CONSOLE_LOGGING === 'true'
}

/**
 * Function to setup Sentry for error monitoring
 */
const setupSentry = () => {
    if (process.env.SENTRY_ENABLED !== 'true') {
        console.warn('Sentry disabled')
        return
    }

    /**
     * Determine Sentry DSN and release based on environment
     */
    const dsn = process.env.SENTRY_DSN
    const environment = isProductionHost() ? 'production' : (isStagingHost() ? 'test' : 'dev')
    const release = process.env.SENTRY_RELEASE_ID
    // If no DSN provided, disable Sentry
    if (!dsn) {
        console.warn('Sentry disabled: No DSN provided')
        return
    }

    // Configure Sentry options
    const confGeneric = {
        dsn,
        release,
        environment,
        integrations: [new BrowserTracing({
            tracingOrigins: isDev()
                ? ['localhost']
                : [window.location.hostname],
            routingInstrumentation: reactRouterV5Instrumentation(history),
        })],
        beforeSend: (data) => {
            data = beforeSendAlterExtras(data)
            data = beforeSendAlterMessage(data)
            data = beforeSendAlterFingerprint(data)
            data = beforeSendFilter(data)

            return data
        },
        beforeBreadcrumb: (breadcrumb, hint) => {
            /**
             * Add custom information to certain types of breadcrumbs
             */
            const original = { ...breadcrumb }

            /**
             * Add track name and track context to the ui.click type of breadcrumbs when clicking link or button.
             * eg:
             * div.context-actions-select__actions-wrapper > ul > li > a.cta - [TrackLabel="trackContext:trackName"]
             */
            try {
                if (breadcrumb && breadcrumb.category === 'ui.click') {
                    const target = get(hint, 'event.target', null)
                    if (target && ['button', 'a'].includes(target.tagName.toLowerCase()) && target.dataset) {
                        const trackContext = target.dataset.trackContext || 'noTrackContext'
                        const trackName = target.dataset.trackName || 'noTrackName'
                        const trackLabel = `${trackContext}:${trackName}`

                        breadcrumb.message = `${breadcrumb.message} - [TrackLabel="${trackLabel}"]`
                    }
                }
            } catch (e) {
                return original
            }

            return breadcrumb
        },
    }

    /**
     * Configure options specific to browser tracing integration
     */
    const confBrowserTracingIntegration = {
        tracesSampleRate: isProductionHost() ? 0.1 : 1.0,
    }

    /**
     * Combine generic and browser tracing options based on environment
     */
    const conf = !isDev() ? {
        ...confGeneric,
        ...confBrowserTracingIntegration,
    } : {
        ...confGeneric,
    }

    console.log('Setup sentry: ', conf)

    init(conf)

    // Set additional metadata for Sentry scope
    configureScope(scope => {
        scope.setExtra('LogMethod', 'None')
        scope.setExtra('Referrer', get(document, 'referrer', '').substring(0, 1000))
    })

    sentryEnabled = true
}

const writeFingerprint = (data, fingerprint) => {
    data.fingerprint = fingerprint
    data.tags = {
        fingerprint: ''
    }

    fingerprint.forEach((fp, i) => {
        const { fingerprint } = data.tags

        if (i > 0) {
            data.tags.fingerprint = `${fingerprint} - ${fp}`
        } else {
            data.tags.fingerprint = fp
        }
    })
}

/**
 * Filter events that we don't want to send to Sentry.
 */
const beforeSendFilter = (data) => {
    let remove = false

    const exception = data?.exception?.values?.[0]

    // Filter "Failed to fetch" events and variations of it as it occurs often when user navigates and fetch is aborted.
    // It has not provided any value in error monitoring so far.
    if (exception && ['Error', 'TypeError'].includes(exception.type) && [
        'Failed to fetch', // Chrome
        'cancelled', // iOS Safari
        'Load failed', // Safari
        'NetworkError when attempting to fetch resource.', // Firefox
    ].includes(exception.value)) {
        remove = true
    }

    return remove ? null : data
}

/**
 * Function to modify data before sending to Sentry based on custom rules
 */
const beforeSendAlterExtras = (data) => {
    // Add extra information to Sentry data
    try {
        const html = document.getElementsByTagName('html')
        if (html[0] && html[0].className.match('translated-ltr')) {
            data.extra = {
                ...data.extra,
                googleTranslated: true,
            }
        }
    } catch (err) {
        console.log(err)
        addBreadcrumb({
            category: 'Warning',
            message: 'beforeSendAlterExtras failed',
            level: Severity.Info,
        })
    }

    return data
}

/**
 * Function to modify error message before sending to Sentry
 */
const beforeSendAlterMessage = (data) => {
    try {
        // Write AppError as error message in case exception error message value is 'undefined'
        // The undefined error comes withing apollo and is not useful when reading logs
        const appError = get(data, 'extra.AppError', null)
        if (appError) {
            const message = `AppError - ${appError.type} - ${appError.code} - ${appError.message}`
            const currentMessage = get(data, 'exception.values[0].value', false)
            if (currentMessage === 'undefined') {
                data.exception.values[0].value = message
            } else {
                addBreadcrumb({
                    category: 'Info',
                    message: 'Exception object not expected format. Cant write error message',
                    level: Severity.Info,
                })
            }
        }
    } catch (err) {
        console.log(err)
        addBreadcrumb({
            category: 'Warning',
            message: 'beforeSendAlterMessage failed',
            level: Severity.Info,
        })
    }

    return data
}

/**
 * Function to apply custom fingerprint rules before sending to Sentry
 */
const beforeSendAlterFingerprint = (data) => {
    try {
        for (const rule of sentryFingerprintRules) {
            // 1) Use function if it's not trivial to match
            // 2) Else, use simple match with default error value path, or custom match path
            if (rule.matchFunction) {
                const fingerprint = rule.matchFunction(data)
                if (fingerprint) {
                    writeFingerprint(data, fingerprint)
                    break
                }
            } else if (rule.matchRegex) {
                const matchPath = !rule.matchPath ? 'exception.values[0].value' : rule.matchPath
                const string = get(data, matchPath, '')
                let isMatch = false

                if (Array.isArray(rule.matchRegex)) {
                    rule.matchRegex.forEach(regex => {
                        if (string && string.match && !!string.match(regex)) {
                            isMatch = true
                        }
                    })
                } else {
                    isMatch = string && string.match && !!string.match(rule.matchRegex)
                }

                if (isMatch) {
                    writeFingerprint(data, rule.fingerprint)
                    break
                }
            }
        }
    } catch (err) {
        console.log(err)
        addBreadcrumb({
            category: 'Warning',
            message: 'beforeSendAlterFingerprint failed',
            level: Severity.Info,
        })
    }

    return data
}

/**
 * Function to update Sentry user scope
 */
const updateSentryUserScope = (almaUser) => {
    // Update user metadata in Sentry scope
    configureScope(scope => {
        const userData = getCurrentUserMetadata(almaUser)
        scope.setUser(userData)
        scope.setExtra('User', userData) // setUser does not support all the values we can add to scope
    })
}

/**
 * Function to set an info breadcrumb in Sentry
 */
const setInfoBreadcrumb = (message) => {
    if (isDev()) console.log('setInfoBreadcrumb - message:', message)

    addBreadcrumb({
        category: 'Info',
        message: message,
        level: Severity.Info,
    })
}

/**
 * Function to set breadcrumbs for Apollo errors in Sentry
 * Add breadcrumbs for Apollo errors in Sentry
 * Omit sensitive fields
 */
const setApolloErrorBreadcrumb = (response, operation, graphQLErrors, networkError) => {
    const excludeFields = [
        /ssn/i,
        /pass/i,
        /pwd/i,
        /mail/i,
        /bank/i,
        /phone/i,
        /mobile/i,
        /token/i,
        /secret/i,
        /key/i,
        /auth/i,
        /cred/i,
        /card/i,
    ]

    const matchExcludeFields = (fieldName) => {
        for (const index in excludeFields) {
            if (!Object.prototype.hasOwnProperty.call(excludeFields, index)) continue

            const excludeFieldName = excludeFields[index]

            if (excludeFieldName instanceof RegExp) {
                if (fieldName && fieldName.match(excludeFieldName)) {
                    return true
                }
            } else {
                if (fieldName && fieldName === excludeFieldName) {
                    return true
                }
            }
        }

        return false
    }

    const traverseExcludeFields = (obj) => {
        for (const fieldName in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, fieldName)) continue

            if (obj[fieldName] && typeof obj[fieldName] === 'object') {
                traverseExcludeFields(obj[fieldName])
            } else {
                if (matchExcludeFields(fieldName)) {
                    const valueLen = obj[fieldName] && obj[fieldName].length
                    delete obj[fieldName]
                    obj[fieldName.split('').join(' ')] = `EXCLUDED_FROM_LOGGING(len:${valueLen})`
                }
            }
        }
    }

    const operationName = get(operation, 'operationName', false)
    if (operationName) {
        addBreadcrumb({
            category: 'Apollo / operationName',
            message: operation ? operation.operationName : 'no operation',
            level: Severity.Info,
        })
    }

    const queryBody = get(operation, 'query.loc.source.body', false)
    if (queryBody) {
        addBreadcrumb({
            category: 'Apollo / queryBody',
            message: queryBody,
            level: Severity.Info,
        })
    }

    const operationVariables = get(operation, 'variables', false)
    if (operationVariables) {
        const logOperationVariables = cloneDeep(operationVariables)
        traverseExcludeFields(logOperationVariables)

        addBreadcrumb({
            category: 'Apollo / operationVariables',
            message: logOperationVariables ? JSON.stringify(logOperationVariables) : 'invalid or no vars',
            level: Severity.Info,
        })
    }
}

/**
 * Function to retrieve current user metadata for Sentry
 * Get metadata for the current user to be sent to Sentry
 * Example: Extract sensitive user information
 */
const getCurrentUserMetadata = (almaUser) => {
    try {
        let data

        try {
            data = Client.cache.readQuery({ query: getCurrentUserForSentry })
        } catch (e) {} // the cache query will always throw if no user

        if (!data || !data.me) {
            return false
        }

        const { email, __typename, ...rest } = data.me

        return {
            email: obscureEmail(email),
            ...rest,
            almaUser: {
                name: almaUser?.name,
                email: almaUser?.email?.replace(/(\w{3})[\w.-]+@([\w.]+\w)/, '$1***@$2'),
                emailVerified: almaUser?.emailVerified,
                role: almaUser?.role,
                id: almaUser?.id,
                accountHash: almaUser?.accountHash,
                almaIdHash: almaUser?.almaIdHash,
                aud: almaUser?.aud,
                nonce: almaUser?.nonce,
                sid: almaUser?.sid,
                updated_at: almaUser?.updated_at,
            },
        }
    } catch (e) {
        console.error(e)
        return null
    }
}

/**
 * Function to get the error handler for app errors
 */
const getAppErrorHandler = () => {
    return sentryEnabled ? (error, appError) => {
        let eventId = null

        if (consoleLoggingEnabled()) {
            console.error(error, 'AppError:', appError)
        }

        if (!appError.message.includes('[HTTP 400]')) {
            withScope(scope => {
                scope.setExtra('LogMethod', 'AppError')
                scope.setExtra('AppError', appError)
                eventId = captureException(error)
            })
        }

        return eventId
    } : console.error
}

/**
 * Function to get the error handler for exceptions
 */
const getExceptionErrorHandler = (logMethod = 'ExceptionError', logFunction = console.error) => {
    return sentryEnabled ? (error) => {
        let eventId = null

        if (consoleLoggingEnabled()) {
            logFunction(error)
        }

        withScope(scope => {
            scope.setExtra('LogMethod', logMethod)
            eventId = captureException(error)
        })

        return eventId
    } : logFunction
}

/**
 * Function to get the error handler for error boundaries
 */
const getErrorBoundaryErrorHandler = () => {
    return sentryEnabled ? (error, errorInfo) => {
        let eventId = null

        if (consoleLoggingEnabled()) {
            console.error(error, errorInfo)
        }

        withScope(scope => {
            scope.setExtra('LogMethod', 'ErrorBoundaryError')
            scope.setExtra('ErrorInfo', errorInfo)
            eventId = captureException(error)

            return eventId
        })
    } : console.error
}

/**
 * Function to get the message handler for debug info
 */
const getDebugInfoMessageHandler = () => {
    return sentryEnabled ? (message) => {
        let eventId = null

        withScope(scope => {
            scope.setExtra('LogMethod', 'InfoMessage')
            eventId = captureMessage(message)
        })

        return eventId
    } : console.log
}

if (!isProductionHost()) {
    window.__mv_sentry = {
        captureException, // For testing purposes to trigger error manually
    }
}

export {
    setupSentry,
    updateSentryUserScope,
    setInfoBreadcrumb,
    setApolloErrorBreadcrumb,
    getAppErrorHandler,
    getExceptionErrorHandler,
    getErrorBoundaryErrorHandler,
    getDebugInfoMessageHandler,
}
